import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './dialog.css';

/**
 * Imperative themed dialog system — replaces native alert()/confirm()/prompt().
 *
 * Usage (async, promise-based):
 *   import { confirmDialog, promptDialog, alertDialog } from '../components/ui/dialog';
 *
 *   if (await confirmDialog({ title: 'Delete task?', message: '...', danger: true })) { ... }
 *   const name = await promptDialog({ title: 'Rename', defaultValue: 'foo' });  // string | null
 *   await alertDialog({ title: 'Failed', message: err, variant: 'error' });
 *
 * A single <DialogHost /> must be mounted once at the app root.
 */

let _dialog = null;
const _listeners = new Set();

function _emit() {
    _listeners.forEach((fn) => fn(_dialog));
}

function _open(config) {
    return new Promise((resolve) => {
        _dialog = { ...config, _resolve: resolve, id: `dlg_${Date.now()}` };
        _emit();
    });
}

function _close(result) {
    if (_dialog && _dialog._resolve) _dialog._resolve(result);
    _dialog = null;
    _emit();
}

export function confirmDialog({
    title = 'Are you sure?',
    message = '',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger = false,
} = {}) {
    return _open({ kind: 'confirm', title, message, confirmLabel, cancelLabel, danger });
}

export function promptDialog({
    title = 'Enter a value',
    message = '',
    defaultValue = '',
    placeholder = '',
    confirmLabel = 'OK',
    cancelLabel = 'Cancel',
} = {}) {
    return _open({ kind: 'prompt', title, message, defaultValue, placeholder, confirmLabel, cancelLabel });
}

export function alertDialog({
    title = 'Notice',
    message = '',
    confirmLabel = 'OK',
    variant = 'info', // 'info' | 'error' | 'success' | 'warning'
} = {}) {
    return _open({ kind: 'alert', title, message, confirmLabel, variant });
}

/**
 * DialogHost — renders the active dialog. Mount once at app root.
 */
export function DialogHost() {
    const [dialog, setDialog] = useState(_dialog);
    const [inputValue, setInputValue] = useState('');
    const overlayRef = useRef(null);
    const inputRef = useRef(null);
    const confirmBtnRef = useRef(null);
    const prevFocusRef = useRef(null);

    useEffect(() => {
        const handler = (d) => {
            setDialog(d);
            if (d && d.kind === 'prompt') setInputValue(d.defaultValue || '');
        };
        _listeners.add(handler);
        return () => _listeners.delete(handler);
    }, []);

    const close = useCallback((result) => _close(result), []);

    // Focus management: capture prior focus, move focus in, restore on close.
    useEffect(() => {
        if (!dialog) return undefined;
        prevFocusRef.current = document.activeElement;
        const focusTarget = dialog.kind === 'prompt' ? inputRef.current : confirmBtnRef.current;
        if (focusTarget) focusTarget.focus();

        return () => {
            const prev = prevFocusRef.current;
            if (prev && typeof prev.focus === 'function') {
                try { prev.focus(); } catch (_) { /* element gone */ }
            }
        };
    }, [dialog]);

    // Escape closes (cancel), focus trap on Tab.
    useEffect(() => {
        if (!dialog) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                close(dialog.kind === 'prompt' ? null : false);
            } else if (e.key === 'Tab') {
                const focusables = overlayRef.current?.querySelectorAll(
                    'button, input, [href], select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (!focusables || focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [dialog, close]);

    if (!dialog) return null;

    const isConfirm = dialog.kind === 'confirm';
    const isPrompt = dialog.kind === 'prompt';
    const isAlert = dialog.kind === 'alert';

    const onConfirm = () => {
        if (isPrompt) close(inputValue);
        else if (isConfirm) close(true);
        else close(undefined);
    };
    const onCancel = () => close(isPrompt ? null : false);

    const dangerClass = dialog.danger || dialog.variant === 'error' ? ' ui-dialog--danger' : '';
    const variantClass = dialog.variant ? ` ui-dialog--${dialog.variant}` : '';

    return createPortal(
        <div
            className="ui-dialog-overlay"
            ref={overlayRef}
            onMouseDown={(e) => { if (e.target === overlayRef.current) onCancel(); }}
        >
            <div
                className={`ui-dialog${dangerClass}${variantClass}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="ui-dialog-title"
            >
                <div className="ui-dialog-title" id="ui-dialog-title">{dialog.title}</div>
                {dialog.message && <div className="ui-dialog-message">{dialog.message}</div>}
                {isPrompt && (
                    <input
                        ref={inputRef}
                        className="ui-dialog-input"
                        type="text"
                        value={inputValue}
                        placeholder={dialog.placeholder || ''}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') onConfirm(); }}
                    />
                )}
                <div className="ui-dialog-actions">
                    {!isAlert && (
                        <button className="ui-dialog-btn ui-dialog-btn--cancel" type="button" onClick={onCancel}>
                            {dialog.cancelLabel}
                        </button>
                    )}
                    <button
                        ref={confirmBtnRef}
                        className={`ui-dialog-btn ui-dialog-btn--confirm${dangerClass ? ' danger' : ''}`}
                        type="button"
                        onClick={onConfirm}
                    >
                        {dialog.confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default DialogHost;
